import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchContext } from '../contexts/SearchContext';
import { fetchFilteredHotels } from '../api-client';
import { Hotel } from '../../../backend/src/models/rateHawkModels/regionSearchResponseModel';
import { HotelDataDumpResponse } from '../../../backend/src/models/rateHawkModels/hotelDumpDataResponseModel';
import { Link } from 'react-router-dom';
import HotelMap from '../components/HotelMap';
import PageLoaderComponent from '../components/Common/PageLoaderSearch';
import { AiFillStar } from 'react-icons/ai';

const RegionSearchResultPage = () => {
  const search = useSearchContext();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [filteredHotels, setFilteredHotels] = useState<HotelDataDumpResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredHotelId, setHoveredHotelId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const [hotelCount, setHotelCount] = useState<number>(0);

  const fetchHotels = async (pageToLoad: number) => {
    const params = {
      checkin: search.checkIn.toISOString().split("T")[0],
      checkout: search.checkOut.toISOString().split("T")[0],
      residency: search.residency || "gb",
      language: "en",
      guests: search.guests.map((g: any) => ({
        adults: g.adults,
        children: g.children || [],
      })),
      region_id: parseInt(search.regionId),
      currency: "GBP",
    };

    //console.log("Fetch Hotel Region Params:", params);

    try {
      setLoading(true);
      const result = await fetchFilteredHotels(params, pageToLoad, 20);
      setHotels((prev) => {
        const existingIds = new Set(prev.map((h) => h.id));
        const newUnique = result.hotels.filter((h) => !existingIds.has(h.id));
        return [...prev, ...newUnique];
      });

  setFilteredHotels((prev) => {
    const existingIds = new Set(prev.map((h: any) => h.id ?? h.data.id));
    const newUnique = result.hotels.filter((h: any) => !existingIds.has(h.id ?? h.data.id));
    return [...prev, ...newUnique];
  });

      setHotelCount(result.totalCount);
      setHasMore(result.hotels.length > 0);
      setLoading(false);
    } catch (err) {
      console.error("Error:", err);
      setError("Failed to load hotels.");
      setLoading(false);
    }
  };

  useEffect(() => {
    setHotels([]);
    setFilteredHotels([]);
    setPage(1);
    setHasMore(true);
    if (search.regionId) fetchHotels(1);
  }, [search.regionId]);

  useEffect(() => {
    if (page > 1) fetchHotels(page);
  }, [page]);

  const lastHotelRef = useCallback((node: HTMLDivElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const HotelCard: React.FC<{ hotel: any; index: number }> = ({ hotel, index }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const formatImageUrl = (url: string) => url.replace("{size}", "640x400");
    const displayedImages = hotel.images?.slice(0, 10) || [];
    const isLast = index === hotels.length - 1;

    // const totalAdults = search.guests.reduce((sum, g) => sum + g.adults, 0);
    // const totalChildren = search.guests.reduce((sum, g) => sum + g.children.length, 0);
    const searchParams = new URLSearchParams({
      checkIn: search.checkIn.toISOString(),
      checkOut: search.checkOut.toISOString(),
      guests: JSON.stringify(search.guests),
      roomCount: search.roomCount.toString(),
      nights: search.nights.toString(),
      vacationType: search.vacationType,
      hotelId: hotel.id,
      regionId: search.regionId,
    }).toString();

    return (
      <div
        ref={isLast ? lastHotelRef : null}
        className="hotel-card flex flex-col md:flex-row border rounded-lg shadow-md bg-white mb-6 md:items-stretch hover:shadow-lg transition-shadow duration-300 overflow-hidden"
        onMouseEnter={() => setHoveredHotelId(hotel.id)}
        onMouseLeave={() => setHoveredHotelId(null)}
      >
        {/* Left Image Panel */}
        <div className="relative w-full md:w-[280px] h-[200px] md:h-auto overflow-hidden">
          {displayedImages.length > 0 ? (
            <>
              <img
                src={formatImageUrl(displayedImages[currentImageIndex])}
                alt={`${hotel.name} - Image ${currentImageIndex + 1}`}
                className="object-cover w-full h-full"
              />
              <button
                className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-black/60 text-white rounded-full px-2 py-1 text-lg"
                onClick={() => setCurrentImageIndex(prev => (prev === 0 ? displayedImages.length - 1 : prev - 1))}
              >
                ‹
              </button>
              <button
                className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-black/60 text-white rounded-full px-2 py-1 text-lg"
                onClick={() => setCurrentImageIndex(prev => (prev === displayedImages.length - 1 ? 0 : prev + 1))}
              >
                ›
              </button>
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                {currentImageIndex + 1} / {displayedImages.length}
              </div>
            </>
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-sm text-gray-500">
              No Image
            </div>
          )}
        </div>

        {/* Right Content Panel */}
        <div className="flex-1 flex flex-col justify-between px-4 py-3">
          <div>
            <div className="flex items-center mb-1">
              {Array.from({ length: hotel?.star_rating || 0 }).map((_, i) => (
                <AiFillStar key={i} color="#facc15" size={14} className="mr-0.5" />
              ))}
            </div>
            <h3 className="text-lg font-bold text-gray-800">{hotel.name}</h3>
            <p className="text-sm text-gray-500 mb-3">{hotel.address}</p>

            {hotel.rates && hotel.rates.length > 0 ? (
              <>
                <p className="text-[15px] font-semibold text-gray-700 mb-1">
                  {hotel.rates[0].room_data_trans.main_name || 'Standard Room'}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm mb-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded capitalize">
                    {hotel.rates[0].meal || 'Meal Not Included'}
                  </span>
                  <span className="text-gray-700 font-medium">
                    {((parseFloat(hotel.rates[0].payment_options[0]?.amount)) * 1.15).toFixed(2)}{' '}
                    {hotel.rates[0].payment_options[0]?.currency || 'GBP'}
                  </span>
                </div>
                <div className="text-xs text-gray-600">
                  <strong>Check-In:</strong> {hotel.check_in_time || 'N/A'}
                  <span className="ml-3">
                    <strong>Check-Out:</strong> {hotel.check_out_time || 'N/A'}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-sm mt-2">No rates available</p>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Link
              to={`/resort/${hotel.id}?${searchParams}&hotelName=${encodeURIComponent(hotel.name)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-halo-pink hover:bg-pink-700 transition-colors text-white text-sm font-semibold rounded px-4 py-2"
            >
              View Deal
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='px-5'>
      {page === 1 && loading && <PageLoaderComponent />}
      {error && <div className="text-red-500 text-center">{error}</div>}

      {hotelCount > 0 && (
        <div className="mb-4 font-bold text-lg">
          {hotelCount} accommodation options available
        </div>
      )}
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/2 h-screen overflow-y-scroll md:pr-2">
          {hotels
            .filter(
              (hotel): hotel is Hotel =>
                hotel && typeof hotel.id !== "undefined"
            )
            .map((hotel, index) => (
              <HotelCard
                key={`${hotel.id}-${index}`}
                hotel={hotel}
                index={index}
              />
            ))}

          {/* Bottom spinner when fetching more pages */}
          {loading && page > 1 && (
            <div className="flex justify-center py-6">
              <svg
                className="animate-spin h-12 w-12 text-halo-pink"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            </div>
          )}
        </div>
        <div className="w-full md:w-1/2 h-screen sticky top-0 hidden md:block">
          <HotelMap hotels={filteredHotels} hoveredHotelId={hoveredHotelId} />
        </div>
      </div>
    </div>
  );
};

export default RegionSearchResultPage;